import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutGrid, 
  UtensilsCrossed, 
  QrCode, 
  Settings, 
  BarChart3, 
  LogOut,
  UserCog,
  Plus,
  ChevronRight,
  Bell,
  ShoppingCart,
  DollarSign,
  Users,
  Clock,
  Sun,
  Moon,
  Menu,
  ChefHat,
  Wallet,
  ClipboardList,
  Monitor,
  Tag,
  MapPin,
  CalendarDays,
  Package,
  Calculator,
  FileText,
  Printer
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '@/utils/api';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import ModernDashboard from '@/modern/admin/ModernDashboard';
import { useIsModern } from '@/modern/shared';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

// Redirecciones por rol y primer ingreso: iguales en los dos temas. Devuelve true si el usuario se queda en Inicio
const useDashboardRedirects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (user?.role !== 'business_admin') return;
    axios.get(`${API_BASE}/onboarding`).then(({ data }) => {
      if (!data.started_at && !data.dismissed && !data.all_done) navigate('/onboarding');
    }).catch(() => {});
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const target = user?.role === 'super_admin' && !user?.active_business_id ? '/super-admin'
    : { kitchen: '/kitchen', cashier: '/cashier', waiter: '/waiter' }[user?.role];
  useEffect(() => { if (target) navigate(target); }, [target, navigate]);
  return !target;
};

export default function Dashboard() {
  const modern = useIsModern();
  const stay = useDashboardRedirects();
  if (!stay) return null;
  return modern ? <ModernDashboard /> : <ClassicDashboard />;
}

function ClassicDashboard() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const { theme, toggleTheme, design, modernDark } = useTheme();
  const showSun = design === 'modern' ? modernDark : theme === 'dark';
  const { connected, lastMessage } = useWebSocket();
  
  const [business, setBusiness] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [todayReservations, setTodayReservations] = useState([]);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    activeUnits: 0
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle real-time updates
  useEffect(() => {
    if (lastMessage?.type === 'new_order') {
      toast.info('¡Nuevo pedido recibido!', {
        description: `Mesa ${lastMessage.unit_id}`,
        action: {
          label: 'Ver',
          onClick: () => navigate('/pos')
        }
      });
      fetchData();
    }
  }, [lastMessage, navigate]);

  const fetchData = async () => {
    try {
      // Get today's date for reservations
      const today = new Date().toISOString().split('T')[0];
      
      const [businessRes, spacesRes, posRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/business`, { withCredentials: true }),
        axios.get(`${API}/spaces`, { withCredentials: true }),
        axios.get(`${API}/pos/dashboard`, { withCredentials: true }),
        axios.get(`${API}/reservations?start_date=${today}&end_date=${today}`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      setBusiness(businessRes.data);
      setSpaces(spacesRes.data);
      setActiveOrders(posRes.data.active_orders || []);
      setTodayReservations(reservationsRes.data || []);
      
      // Calculate stats
      const activeUnits = posRes.data.spaces?.reduce((acc, space) => {
        return acc + (space.units?.filter(u => u.status === 'occupied').length || 0);
      }, 0) || 0;

      setStats({
        totalSales: 0, // Would come from reports
        totalOrders: posRes.data.active_orders?.length || 0,
        activeUnits
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleExitBusiness = async () => {
    if (user?.role === 'super_admin') {
      try {
        await axios.post(`${API}/admin/exit-business`);
        await checkAuth();
        navigate('/super-admin');
      } catch (error) {
        toast.error('Error al salir');
      }
    }
  };

  const navItems = [
    { icon: LayoutGrid, label: 'Dashboard', path: '/dashboard', active: true },
    { icon: QrCode, label: 'Panel POS', path: '/pos' },
    { icon: UtensilsCrossed, label: 'Menú', path: '/menu-editor' },
    { icon: LayoutGrid, label: 'Espacios', path: '/layout-editor' },
    { icon: MapPin, label: 'Mapa Visual', path: '/visual-map' },
    { icon: QrCode, label: 'Centro de QRs', path: '/qr-center' },
    { icon: CalendarDays, label: 'Reservas', path: '/reservations' },
    { icon: Tag, label: 'Promociones', path: '/promotions' },
    { icon: Calculator, label: 'Caja', path: '/cash-register' },
    { icon: FileText, label: 'Facturación', path: '/billing' },
    { icon: Printer, label: 'Diseñador Docs', path: '/template-designer' },
    { icon: Package, label: 'Inventario', path: '/inventory' },
    { icon: Clock, label: 'Turnos', path: '/shifts' },
    { icon: BarChart3, label: 'Reportes', path: '/reports' },
    { icon: Settings, label: 'Configuración', path: '/settings' },
  ];

  const staffViews = [
    { icon: Wallet, label: 'Vista Caja', path: '/cashier', color: 'emerald' },
    { icon: ClipboardList, label: 'Vista Mozo', path: '/waiter', color: 'violet' },
    { icon: ChefHat, label: 'Vista Cocina', path: '/kitchen', color: 'orange' },
    { icon: Monitor, label: 'Pantalla Cocina TV', path: '/kitchen-tv', color: 'red' },
  ];

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? 'p-4' : 'w-64 border-r border-border h-screen sticky top-0'} flex flex-col bg-card app-sidebar`} data-testid="app-sidebar">
      {/* Fixed Header */}
      <div className="p-6 pb-0">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2 mb-6">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user?.role === 'super_admin' ? 'bg-red-600' : 'bg-primary'}`}>
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-xl font-semibold">QR Mesa</span>
        </Link>

        {/* Super Admin Badge */}
        {user?.role === 'super_admin' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-xs text-red-600 font-medium">MODO SUPER ADMIN</p>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2 text-red-600 hover:bg-red-100"
              onClick={handleExitBusiness}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Volver al Panel Admin
            </Button>
          </div>
        )}

        {/* Business Name */}
        {business && (
          <div className="mb-4 pb-4 border-b border-border">
            <p className="text-sm text-muted-foreground">
              {user?.role === 'super_admin' ? 'Configurando' : 'Tu negocio'}
            </p>
            <p className="font-semibold truncate">{business.name}</p>
          </div>
        )}
      </div>

      {/* Scrollable Navigation */}
      <nav className="flex-1 overflow-y-auto px-6 pb-6 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => mobile && setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              item.active 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}

        {/* Staff Views Divider */}
        <div className="pt-4 mt-4 border-t border-border">
          <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Vistas de Personal
          </p>
          {staffViews.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => mobile && setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors text-muted-foreground hover:bg-muted hover:text-foreground`}
            >
              <item.icon className={`w-5 h-5 ${
                item.color === 'emerald' ? 'text-emerald-500' :
                item.color === 'violet' ? 'text-violet-500' :
                item.color === 'orange' ? 'text-orange-500' : ''
              }`} />
              <span className="font-medium text-sm">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* User Section */}
      <div className="p-6 pt-4 border-t border-border mt-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <Users className="w-5 h-5 text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={() => navigate('/perfil')}
          data-testid="profile-link"
        >
          <UserCog className="w-4 h-4" />
          Mi perfil y apariencia
        </Button>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Cerrar Sesión
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile Header & Menu */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <QrCode className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-heading font-semibold">QR Mesa</span>
        </Link>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-mobile">
            {showSun ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <Sidebar mobile />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:p-8 p-4 pt-20 lg:pt-8">
        <OnboardingChecklist />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              ¡Hola, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">
              Aquí tienes el resumen de tu negocio
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
              {connected ? 'Conectado' : 'Desconectado'}
            </div>
            
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="hidden lg:flex" data-testid="theme-toggle">
              {showSun ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pedidos Activos</p>
                  <p className="text-2xl font-bold">{stats.totalOrders}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Mesas Ocupadas</p>
                  <p className="text-2xl font-bold">{stats.activeUnits}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <LayoutGrid className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Espacios</p>
                  <p className="text-2xl font-bold">{spaces.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tiempo Prom.</p>
                  <p className="text-2xl font-bold">15m</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="card-interactive" onClick={() => navigate('/pos')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Panel POS</h3>
                    <p className="text-sm text-muted-foreground">Gestiona pedidos en tiempo real</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-interactive" onClick={() => navigate('/menu-editor')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                    <UtensilsCrossed className="w-6 h-6 text-secondary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Editar Menú</h3>
                    <p className="text-sm text-muted-foreground">Productos y categorías</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-interactive" onClick={() => navigate('/layout-editor')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center">
                    <LayoutGrid className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Diseñar Espacios</h3>
                    <p className="text-sm text-muted-foreground">Layout de mesas y zonas</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Spaces List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Tus Espacios</CardTitle>
              <CardDescription>Gestiona las zonas de tu negocio</CardDescription>
            </div>
            <Button onClick={() => navigate('/layout-editor')} data-testid="add-space-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Espacio
            </Button>
          </CardHeader>
          <CardContent>
            {spaces.length === 0 ? (
              <div className="text-center py-12">
                <LayoutGrid className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No hay espacios creados</h3>
                <p className="text-muted-foreground mb-4">
                  Crea tu primer espacio para comenzar a agregar mesas
                </p>
                <Button onClick={() => navigate('/layout-editor')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Espacio
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {spaces.map((space) => (
                  <Card 
                    key={space.space_id} 
                    className="card-interactive"
                    onClick={() => navigate(`/layout-editor/${space.space_id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{space.name}</h4>
                          <p className="text-sm text-muted-foreground capitalize">
                            {space.space_type}
                          </p>
                        </div>
                        <Badge variant="secondary">
                          {space.space_type === 'beach' ? 'Playa' : 
                           space.space_type === 'outdoor' ? 'Exterior' :
                           space.space_type === 'terrace' ? 'Terraza' : 'Interior'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        {activeOrders.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Pedidos Recientes</CardTitle>
              <CardDescription>Últimos pedidos pendientes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activeOrders.slice(0, 5).map((order) => (
                  <div 
                    key={order.order_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate('/pos')}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Pedido #{order.order_id.slice(-6)}</p>
                        <p className="text-sm text-muted-foreground">
                          {order.items?.length || 0} items • ${order.total?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant="outline"
                      style={{ 
                        backgroundColor: order.status === 'Nuevo' ? '#DBEAFE' : 
                                        order.status === 'Confirmado' ? '#E9D5FF' :
                                        order.status === 'En preparación' ? '#FEF3C7' : '#D1FAE5',
                        color: '#17171C'
                      }}
                    >
                      {order.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Reservations */}
        <Card className="mt-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Reservas de Hoy
              </CardTitle>
              <CardDescription>
                {todayReservations.length} reservas para hoy
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/reservations')}>
              Ver todas
            </Button>
          </CardHeader>
          <CardContent>
            {todayReservations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay reservas para hoy</p>
                <Button 
                  variant="link" 
                  onClick={() => navigate('/reservations')}
                  className="mt-2"
                >
                  Crear una reserva
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {todayReservations
                  .sort((a, b) => a.reservation_time.localeCompare(b.reservation_time))
                  .slice(0, 6)
                  .map((reservation) => (
                  <div 
                    key={reservation.reservation_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate('/reservations')}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center font-mono ${
                        reservation.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        reservation.status === 'seated' ? 'bg-blue-100 text-blue-700' :
                        reservation.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        <span className="text-lg font-bold">{reservation.reservation_time.split(':')[0]}</span>
                        <span className="text-xs">{reservation.reservation_time.split(':')[1]}</span>
                      </div>
                      <div>
                        <p className="font-medium">{reservation.customer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {reservation.party_size} personas • {reservation.unit_name || 'Mesa'}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant="outline"
                      className={
                        reservation.status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-200' :
                        reservation.status === 'seated' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        reservation.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        reservation.status === 'completed' ? 'bg-gray-50 text-gray-700 border-gray-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }
                    >
                      {reservation.status === 'confirmed' ? 'Confirmada' :
                       reservation.status === 'seated' ? 'Sentado' :
                       reservation.status === 'pending' ? 'Pendiente' :
                       reservation.status === 'completed' ? 'Completada' :
                       reservation.status === 'no_show' ? 'No vino' : 'Cancelada'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
